import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LIBRARIES, { LIBRARY_ORDER } from "@/lib/libraryConfig";
import { listLibrary, seedLibraries } from "@/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Database } from "lucide-react";

export default function Dashboard() {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);

  const fetchCounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(
        LIBRARY_ORDER.map((key) =>
          listLibrary(key).then((items) => [key, items.length])
        )
      );
      setCounts(Object.fromEntries(results));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async (force) => {
    setSeeding(true);
    try {
      await seedLibraries(force);
      await fetchCounts();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="dashboard-title">
          Dashboard
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage your VoyageMenu library collections.
          {!loading && (
            <span className="ml-1">
              <Badge variant="secondary" data-testid="total-items-badge">
                {total} items total
              </Badge>
            </span>
          )}
        </p>
      </div>

      {/* Actions */}
      <div className="mb-6 flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={fetchCounts}
          disabled={loading}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleSeed(false)}
          disabled={seeding}
          data-testid="seed-btn"
        >
          {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
          Seed Data
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => handleSeed(true)}
          disabled={seeding}
          data-testid="force-seed-btn"
        >
          {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
          Force Re-Seed
        </Button>
      </div>

      {error && (
        <div
          className="mb-6 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive"
          data-testid="dashboard-error"
        >
          {error}
        </div>
      )}

      {/* Library cards grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="library-grid">
        {LIBRARY_ORDER.map((key) => {
          const lib = LIBRARIES[key];
          const Icon = lib.icon;
          const count = counts[key];
          return (
            <Link
              key={key}
              to={`/library/${key}`}
              data-testid={`lib-card-${key}`}
              className="group flex flex-col gap-3 rounded-lg border bg-card p-5 transition-all hover:shadow-md hover:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex items-center justify-between">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                {count !== undefined && (
                  <Badge variant="secondary" data-testid={`lib-count-${key}`}>
                    {count}
                  </Badge>
                )}
                {loading && count === undefined && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold group-hover:text-primary transition-colors">
                  {lib.label}
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                  {lib.description}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
